import "bootstrap/dist/css/bootstrap.min.css";
import { get, ref, set, push, remove, onValue, update as fbUpdate } from "firebase/database";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { db } from '../firebase.js';
import { logout } from '../store/authSlice.js';
import { updateOrder, subscribeOrders } from '../store/ordersSlice.js';
import { createProduct, deleteProduct, updateProduct } from '../store/productsSlice.js';
import { downloadBillAsPDF } from '../utils/billUtils.js';
import AdminSpecialOffers from './AdminSpecialOffers.js';
import AdminOrderManagement from './AdminOrderManagement.js';
import AdminRatingsManager from './AdminRatingsManager.js';
import ThemeManager from './ThemeManager.js';
import Bill from './Bill.js';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  // selector returns state may be an object if API response was malformed
  const rawOrders = useSelector((s) => s.orders.items);
  const orders = Array.isArray(rawOrders)
    ? rawOrders
    : rawOrders && typeof rawOrders === 'object'
    ? Object.values(rawOrders)
    : [];
  const products = useSelector((s) => Array.isArray(s.products.items) ? s.products.items : []);
  const navigate = useNavigate();

  // Subscribe to ALL orders in real-time (admin sees everything)
  useEffect(() => {
    dispatch(subscribeOrders(null)); // null = no userId filter
  }, [dispatch]);

  const [activeTab, setActiveTab] = useState("dashboard");
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBill, setShowBill] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [trackingLink, setTrackingLink] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "all",
    search: "",
    sortBy: "date",
    sortOrder: "desc"
  });
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [form, setForm] = useState({
    name: "",
    description: "",
    type: "Premium",
    gender: "Male",
    price: "",
    oldPrice: "",
    images: [],   // array of base64 strings
    volume: "50ml",
    longevity: "12+ Hours",
    sillage: "High",
    season: "All Year",
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [customTypeMode, setCustomTypeMode] = useState(false);
  const [customTypeInput, setCustomTypeInput] = useState("");

  const DEFAULT_TYPES = ["Premium", "Luxury", "Eau de Parfum"];
  const [extraTypes, setExtraTypes] = useState([]);
  // allTypes = defaults + types from Firebase + types already on saved products
  const allTypes = [...new Set([...DEFAULT_TYPES, ...extraTypes, ...products.map(p => p.type).filter(Boolean)])];

  // Load custom types from Firebase in real-time
  useEffect(() => {
    if (!db) return;
    const unsub = onValue(ref(db, "productTypes"), (snap) => {
      if (snap.exists()) {
        const val = snap.val();
        // stored as { key: typeName }
        setExtraTypes(Object.values(val).filter(Boolean));
      } else {
        setExtraTypes([]);
      }
    });
    return () => unsub();
  }, []);

  const handleTypeChange = (e) => {
    const val = e.target.value;
    if (val === "__custom__") {
      setCustomTypeMode(true);
      setCustomTypeInput("");
    } else {
      setCustomTypeMode(false);
      setForm(prev => ({ ...prev, type: val }));
    }
  };

  const confirmCustomType = async () => {
    const trimmed = customTypeInput.trim();
    if (!trimmed) return;
    // Save to Firebase so it persists forever
    if (db && !allTypes.includes(trimmed)) {
      try {
        const newRef = push(ref(db, "productTypes"));
        await set(newRef, trimmed);
      } catch (e) {
        // onValue will still update extraTypes; optimistically update too
        setExtraTypes(prev => [...new Set([...prev, trimmed])]);
      }
    }
    setForm(prev => ({ ...prev, type: trimmed }));
    setCustomTypeMode(false);
    setCustomTypeInput("");
  };

  const deleteCustomType = async (typeName) => {
    if (!db) return;
    try {
      // Find the key for this type value in Firebase
      const snap = await get(ref(db, "productTypes"));
      if (snap.exists()) {
        const val = snap.val();
        const key = Object.keys(val).find(k => val[k] === typeName);
        if (key) await remove(ref(db, `productTypes/${key}`));
      }
      // If current form type was deleted, reset to first default
      if (form.type === typeName) setForm(prev => ({ ...prev, type: DEFAULT_TYPES[0] }));
    } catch (e) {
      toast.error("Failed to delete type");
    }
  };

  // Banner state
  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({ title: '', text: '', image: '', animation: 'animate__fadeInLeft' });
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerImageUploading, setBannerImageUploading] = useState(false);
  const ANIMATIONS = ['animate__fadeInLeft', 'animate__fadeInRight', 'animate__fadeInUp', 'animate__fadeInDown'];

  const handleBannerImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB.');
      e.target.value = '';
      return;
    }
    setBannerImageUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        // Resize to max 1200px wide, compress to 0.75 quality
        const MAX_W = 1200;
        const scale = img.width > MAX_W ? MAX_W / img.width : 1;
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.75);
        setBannerForm((prev) => ({ ...prev, image: compressed }));
        setBannerImageUploading(false);
      };
      img.onerror = () => setBannerImageUploading(false);
      img.src = reader.result;
    };
    reader.onerror = () => setBannerImageUploading(false);
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const oversized = files.filter((f) => f.size > 500 * 1024);
    if (oversized.length) {
      toast.error(`${oversized.length} image(s) exceed 500KB. Please compress them.`);
      e.target.value = "";
      return;
    }

    setImageUploading(true);
    let loaded = 0;
    const results = [];

    files.forEach((file, idx) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        results[idx] = reader.result;
        loaded++;
        if (loaded === files.length) {
          setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...results] }));
          setImageUploading(false);
        }
      };
      reader.onerror = () => {
        loaded++;
        if (loaded === files.length) setImageUploading(false);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
    if (activeTab === "banners") fetchBanners();
  }, [dispatch, activeTab]);

  const fetchBanners = async () => {
    try {
      if (!db) return;
      const snapshot = await get(ref(db, 'banners'));
      if (snapshot.exists()) {
        const val = snapshot.val();
        setBanners(Object.keys(val).map((k) => ({ id: k, ...val[k] })));
      } else {
        setBanners([]);
      }
    } catch (e) {
      if (e?.code !== 'PERMISSION_DENIED') toast.error('Failed to fetch banners');
    }
  };

  const saveBanner = async () => {
    if (!bannerForm.title || !bannerForm.image) return toast.warning('Title and Image are required');
    setBannerLoading(true);
    try {
      if (editingBanner) {
        await set(ref(db, `banners/${editingBanner.id}`), { ...editingBanner, ...bannerForm, updatedAt: Date.now() });
        toast.success('Banner updated');
      } else {
        const newRef = push(ref(db, 'banners'));
        await set(newRef, { id: newRef.key, ...bannerForm, createdAt: Date.now() });
        toast.success('Banner added');
      }
      setBannerForm({ title: '', text: '', image: '', animation: 'animate__fadeInLeft' });
      setEditingBanner(null);
      fetchBanners();
    } catch { toast.error('Failed to save banner'); }
    finally { setBannerLoading(false); }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await remove(ref(db, `banners/${id}`));
      toast.success('Banner deleted');
      fetchBanners();
    } catch { toast.error('Failed to delete banner'); }
  };

  const setFirstBanner = async (id) => {
    try {
      const updates = {};
      banners.forEach((b) => { updates[`banners/${b.id}/isFirst`] = b.id === id; });
      await fbUpdate(ref(db, '/'), updates);
      toast.success('First banner updated');
      fetchBanners();
    } catch { toast.error('Failed to set first banner'); }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      if (db) {
        const usersRef = ref(db, 'users');
        const snapshot = await get(usersRef);
        if (snapshot.exists()) {
          const usersData = snapshot.val();
          const usersList = Object.keys(usersData).map(uid => ({
            uid,
            ...usersData[uid]
          }));
          setUsers(usersList);
        } else {
          setUsers([]);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const getAvatarUrl = (email) => {
    const seed = email ? email.split('@')[0] : 'user';
    return `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${seed}`;
  };

  if (!user) {
    navigate("/admin/login");
    return null;
  }

  // Filter and sort orders
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (filters.status !== "all" && order.status !== filters.status) return false;
    
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const searchFields = [
        order.name,
        order.email,
        order.phone,
        order.id,
        order.address,
        order.city
      ].filter(Boolean).join(" ").toLowerCase();
      if (!searchFields.includes(searchTerm)) return false;
    }
    
    // Date range filter
    if (filters.dateRange === "custom" && (dateRange.start || dateRange.end)) {
      const orderDate = new Date(order.createdAt);
      if (dateRange.start && orderDate < new Date(dateRange.start)) return false;
      if (dateRange.end && orderDate > new Date(dateRange.end + "T23:59:59")) return false;
    } else if (filters.dateRange !== "all" && filters.dateRange !== "custom") {
      const now = new Date();
      const orderDate = new Date(order.createdAt);
      let startDate;
      
      switch (filters.dateRange) {
        case "today":
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case "week":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "month":
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case "quarter":
          startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          break;
        default:
          startDate = null;
      }
      
      if (startDate && orderDate < startDate) return false;
    }
    
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    
    switch (filters.sortBy) {
      case "date":
        aVal = new Date(a.createdAt || 0);
        bVal = new Date(b.createdAt || 0);
        break;
      case "amount":
        aVal = a.total || 0;
        bVal = b.total || 0;
        break;
      case "customer":
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
        break;
      case "status":
        aVal = a.status || "pending";
        bVal = b.status || "pending";
        break;
      default:
        aVal = a.id || "";
        bVal = b.id || "";
    }
    
    if (filters.sortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const totalOrders = orders.length;
  const totalProducts = products.length; // eslint-disable-line no-unused-vars
  // Treat confirmed/pending/processing as "active/pending"; completed/delivered as done
  const pendingOrders = orders.filter((o) => ["pending", "confirmed", "processing"].includes(o.status || "confirmed")).length;
  const completedOrders = orders.filter((o) => ["completed", "delivered"].includes(o.status)).length;

  // Real trend: compare current month vs last month
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const lastMonthEnd   = thisMonthStart - 1;

  const thisMonthOrders = orders.filter((o) => (o.createdAt || 0) >= thisMonthStart);
  const lastMonthOrders = orders.filter((o) => (o.createdAt || 0) >= lastMonthStart && (o.createdAt || 0) <= lastMonthEnd);

  const calcTrend = (curr, prev) => {
    if (prev === 0) return curr > 0 ? "+100%" : "0%";
    const pct = Math.round(((curr - prev) / prev) * 100);
    return (pct >= 0 ? "+" : "") + pct + "%";
  };

  const trendOrders  = calcTrend(thisMonthOrders.length, lastMonthOrders.length);
  const trendPending = calcTrend(
    thisMonthOrders.filter((o) => ["pending","confirmed","processing"].includes(o.status || "confirmed")).length,
    lastMonthOrders.filter((o) => ["pending","confirmed","processing"].includes(o.status || "confirmed")).length
  );
  const trendCompleted = calcTrend(
    thisMonthOrders.filter((o) => ["completed","delivered"].includes(o.status)).length,
    lastMonthOrders.filter((o) => ["completed","delivered"].includes(o.status)).length
  );
  const trendRevenue = calcTrend(
    thisMonthOrders.reduce((s, o) => s + (o.total || 0), 0),
    lastMonthOrders.reduce((s, o) => s + (o.total || 0), 0)
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
    toast.success("Logged out");
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      dateRange: "all",
      search: "",
      sortBy: "date",
      sortOrder: "desc"
    });
    setDateRange({ start: "", end: "" });
    setCurrentPage(1);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setCustomTypeMode(false);
    setCustomTypeInput("");
    setForm({
      name: "", description: "", type: "Premium", gender: "Male",
      price: "", oldPrice: "", images: [], volume: "50ml",
      longevity: "12+ Hours", sillage: "High", season: "All Year",
    });
    setShowProductForm(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();

    if (!form.name || !form.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingProduct) {
        await dispatch(
          updateProduct({ id: editingProduct, data: form }),
        ).unwrap();
        toast.success("Product updated");
      } else {
        await dispatch(createProduct(form)).unwrap();
        toast.success("Product created");
      }
      setShowProductForm(false);
      setForm({
        name: "", description: "", type: "Premium", gender: "Male",
        price: "", oldPrice: "", images: [], volume: "50ml",
        longevity: "12+ Hours", sillage: "High", season: "All Year",
      });
    } catch (err) {
      toast.error(err || "Failed to save product");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product.id);
    setCustomTypeMode(false);
    setCustomTypeInput("");
    setForm({
      ...product,
      images: product.images || (product.image ? [product.image] : []),
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm("Delete this product?")) {
      try {
        await dispatch(deleteProduct(id)).unwrap();
        toast.success("Product deleted");
      } catch (err) {
        toast.error(err || "Failed to delete product");
      }
    }
  };

  const handleViewBill = (order) => {
    setSelectedOrder(order);
    setShowBill(true);
  };

  const handleDownloadBill = () => {
    if (selectedOrder) {
      downloadBillAsPDF(selectedOrder.orderId || selectedOrder.id);
    }
  };

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setTrackingLink("");
    setShowOrderDetails(true);
  };

  const handleSendTrackingEmail = async () => {
    if (!trackingLink.trim()) {
      toast.error("Please enter a tracking link");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch("http://localhost:3001/api/send-tracking-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: selectedOrder.email,
          trackingDetails: {
            orderId: selectedOrder.id?.slice(0, 8) || "N/A",
            customerName: selectedOrder.name,
            trackingUrl: trackingLink,
            courierName: "Express Delivery",
            estimatedDelivery: "2-3 business days"
          }
        })
      });

      if (response.ok) {
        await updateOrderStatus(selectedOrder.id, "completed");
        toast.success("Tracking email sent and order marked as completed!");
        setShowOrderDetails(false);
      } else {
        toast.error("Failed to send tracking email");
      }
    } catch (error) {
      console.error("Error sending tracking email:", error);
      toast.error("Error sending tracking email");
    } finally {
      setSendingEmail(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const orderToUpdate = orders.find(o => o.id === orderId);
      if (orderToUpdate) {
        const updatedOrder = { ...orderToUpdate, status };
        await dispatch(updateOrder({ id: orderId, data: updatedOrder })).unwrap();
      }
      
      const response = await fetch(`http://localhost:3001/api/update-order-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, status })
      });
      return response.ok;
    } catch (error) {
      console.error("Error updating order status:", error);
      return false;
    }
  };

  return (
    <div className="d-flex min-vh-100" style={{ background: "#f8f9fa" }}>
      {/* SIDEBAR */}
      <div
        className="p-4"
        style={{
          width: "280px",
          background: "#2f0f0c",
          color: "white",
          minHeight: "100vh",
        }}
      >
        <h3 className="fw-bold mb-5">
          <i className="bi bi-speedometer2 me-2"></i>Admin
        </h3>

        <nav className="d-flex flex-column gap-2">
          {[
            { id: "dashboard", label: "Dashboard", icon: "speedometer2" },
            { id: "orders", label: "Orders", icon: "receipt" },
            { id: "products", label: "Products", icon: "box" },
            { id: "users", label: "User Details", icon: "people" },
            { id: "ratings", label: "Ratings", icon: "star-fill" },
            { id: "special-offers", label: "Special Offers", icon: "star" },
            { id: "analytics", label: "Analytics", icon: "graph-up" },
            { id: "banners", label: "Banners", icon: "image" },
            { id: "theme", label: "Theme / Colors", icon: "palette" },
          ].map((item) => (
            <button
              key={item.id}
              className={`btn text-start px-3 py-2 rounded-3 ${
                activeTab === item.id
                  ? "btn-light text-dark"
                  : "btn-outline-light text-white"
              }`}
              onClick={() => setActiveTab(item.id)}
            >
              <i className={`bi bi-${item.icon} me-2`}></i>
              {item.label}
            </button>
          ))}
        </nav>

        <hr className="my-4 bg-white" />

        <div className="text-white-50 small mb-3">
          <p className="mb-1">
            <strong>Admin:</strong> {user.name || user.email}
          </p>
          <p className="mb-3">{user.email}</p>
        </div>

        <button
          className="btn btn-outline-danger w-100 rounded-3"
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right me-2"></i>Logout
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="fw-bold">Admin Dashboard</h1>
          <div className="text-muted">{new Date().toLocaleDateString()}</div>
        </div>

        {/* DASHBOARD TAB */}
        {activeTab === "dashboard" && (
          <div>
            {/* STATS */}
            <div className="row g-4 mb-5">
              {[
                {
                  title: "Total Orders",
                  value: totalOrders,
                  icon: "receipt",
                  color: "#667eea",
                  trend: trendOrders,
                  subtitle: "vs last month"
                },
                {
                  title: "Pending Orders",
                  value: pendingOrders,
                  icon: "hourglass-split",
                  color: "#f59e0b",
                  trend: trendPending,
                  subtitle: "need attention"
                },
                {
                  title: "Completed Orders",
                  value: completedOrders,
                  icon: "check-circle",
                  color: "#10b981",
                  trend: trendCompleted,
                  subtitle: "this month"
                },
                {
                  title: "Total Revenue",
                  value: `₹${totalRevenue.toLocaleString()}`,
                  icon: "currency-rupee",
                  color: "#ef4444",
                  trend: trendRevenue,
                  subtitle: "vs last month"
                },
              ].map((stat, idx) => (
                <div key={idx} className="col-md-6 col-xl-3">
                  <div className="card border-0 shadow-sm rounded-4 p-4 h-100">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <p className="text-muted small mb-1 fw-medium">{stat.title}</p>
                        <h3 className="fw-bold mb-1">{stat.value}</h3>
                        <div className="d-flex align-items-center">
                          <span className={`badge small me-2 ${stat.trend.startsWith('-') ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>{stat.trend}</span>
                          <small className="text-muted">{stat.subtitle}</small>
                        </div>
                      </div>
                      <div
                        className="rounded-3 p-3 d-flex align-items-center justify-content-center"
                        style={{ background: stat.color + "15", width: "60px", height: "60px" }}
                      >
                        <i
                          className={`bi bi-${stat.icon}`}
                          style={{ color: stat.color, fontSize: "24px" }}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* QUICK ACTIONS */}
            <div className="row g-4 mb-5">
              <div className="col-md-8">
                <div className="card border-0 shadow-sm rounded-4 p-4">
                  <h5 className="fw-bold mb-4">Recent Orders</h5>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0 fw-semibold">Order ID</th>
                          <th className="border-0 fw-semibold">Customer</th>
                          <th className="border-0 fw-semibold">Amount</th>
                          <th className="border-0 fw-semibold">Status</th>
                          <th className="border-0 fw-semibold">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.slice(-5).reverse().map((order) => (
                          <tr key={order.id}>
                            <td className="fw-bold text-primary">
                              #{order.id?.slice(0, 8)}
                            </td>
                            <td>
                              <div>
                                <div className="fw-medium">{order.name || "—"}</div>
                                <small className="text-muted">{order.email}</small>
                              </div>
                            </td>
                            <td className="fw-bold">₹{order.total?.toLocaleString() || "—"}</td>
                            <td>
                              <span
                                className={`badge rounded-pill ${
                                  (order.status || "pending") === "pending"
                                    ? "bg-warning-subtle text-warning"
                                    : "bg-success-subtle text-success"
                                }`}
                              >
                                {order.status || "pending"}
                              </span>
                            </td>
                            <td className="text-muted small">
                              {order.createdAt
                                ? new Date(order.createdAt).toLocaleDateString()
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                  <h6 className="fw-bold mb-3">Quick Actions</h6>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-outline-primary rounded-3"
                      onClick={() => setActiveTab("orders")}
                    >
                      <i className="bi bi-receipt me-2"></i>Manage Orders
                    </button>
                    <button 
                      className="btn btn-outline-success rounded-3"
                      onClick={() => setActiveTab("products")}
                    >
                      <i className="bi bi-box me-2"></i>Manage Products
                    </button>
                    <button 
                      className="btn btn-outline-info rounded-3"
                      onClick={() => setActiveTab("analytics")}
                    >
                      <i className="bi bi-graph-up me-2"></i>View Analytics
                    </button>
                  </div>
                </div>
                <div className="card border-0 shadow-sm rounded-4 p-4">
                  <h6 className="fw-bold mb-3">System Status</h6>
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-success rounded-circle me-2" style={{width: "8px", height: "8px"}}></div>
                    <small className="text-muted">All systems operational</small>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-success rounded-circle me-2" style={{width: "8px", height: "8px"}}></div>
                    <small className="text-muted">Email service active</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="bg-success rounded-circle me-2" style={{width: "8px", height: "8px"}}></div>
                    <small className="text-muted">Payment gateway online</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === "orders" && (
          <AdminOrderManagement />
        )}

        {/* RATINGS TAB */}
        {activeTab === "ratings" && (
          <AdminRatingsManager />
        )}

        {/* OLD ORDERS TAB - BACKUP */}
        {activeTab === "orders-old" && (
          <div>
            {/* FILTERS SECTION */}
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label small fw-semibold text-muted">Search Orders</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 bg-light"
                      placeholder="Search by name, email, ID..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange("search", e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold text-muted">Status</label>
                  <select
                    className="form-select bg-light"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold text-muted">Date Range</label>
                  <select
                    className="form-select bg-light"
                    value={filters.dateRange}
                    onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold text-muted">Sort By</label>
                  <select
                    className="form-select bg-light"
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                  >
                    <option value="date">Date</option>
                    <option value="amount">Amount</option>
                    <option value="customer">Customer</option>
                    <option value="status">Status</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-semibold text-muted">Order</label>
                  <select
                    className="form-select bg-light"
                    value={filters.sortOrder}
                    onChange={(e) => handleFilterChange("sortOrder", e.target.value)}
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
                <div className="col-md-1">
                  <label className="form-label small fw-semibold text-muted">&nbsp;</label>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
              </div>
              
              {filters.dateRange === "custom" && (
                <div className="row g-3 mt-2">
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold text-muted">Start Date</label>
                    <input
                      type="date"
                      className="form-control bg-light"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold text-muted">End Date</label>
                    <input
                      type="date"
                      className="form-control bg-light"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              
              <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                <div className="text-muted small">
                  Showing {paginatedOrders.length} of {filteredOrders.length} orders
                  {filters.search && ` matching "${filters.search}"`}
                </div>
                <div className="d-flex gap-2">
                  <span className="badge bg-primary-subtle text-primary px-3 py-2">
                    Total: {filteredOrders.length}
                  </span>
                  <span className="badge bg-warning-subtle text-warning px-3 py-2">
                    Pending: {filteredOrders.filter(o => (o.status || "pending") === "pending").length}
                  </span>
                  <span className="badge bg-success-subtle text-success px-3 py-2">
                    Completed: {filteredOrders.filter(o => o.status === "completed").length}
                  </span>
                </div>
              </div>
            </div>

            {/* ORDERS TABLE */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0 fw-semibold">Order ID</th>
                      <th className="border-0 fw-semibold">Customer</th>
                      <th className="border-0 fw-semibold">Contact</th>
                      <th className="border-0 fw-semibold">Items</th>
                      <th className="border-0 fw-semibold">Amount</th>
                      <th className="border-0 fw-semibold">Status</th>
                      <th className="border-0 fw-semibold">Date</th>
                      <th className="border-0 fw-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.length > 0 ? paginatedOrders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <div className="fw-bold text-primary">#{order.id?.slice(0, 8)}</div>
                          <small className="text-muted">{order.id?.slice(8, 16)}</small>
                        </td>
                        <td>
                          <div className="fw-medium">{order.name || "—"}</div>
                          <small className="text-muted">{order.email}</small>
                        </td>
                        <td>
                          <div className="small">{order.phone || "—"}</div>
                          <small className="text-muted">{order.city}, {order.state}</small>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {order.items?.length || 0} item(s)
                          </span>
                        </td>
                        <td className="fw-bold text-success">
                          ₹{order.total?.toLocaleString() || "—"}
                        </td>
                        <td>
                          <span
                            className={`badge rounded-pill ${
                              (order.status || "pending") === "pending"
                                ? "bg-warning-subtle text-warning"
                                : "bg-success-subtle text-success"
                            }`}
                          >
                            <i className={`bi bi-${
                              (order.status || "pending") === "pending" ? "hourglass-split" : "check-circle"
                            } me-1`}></i>
                            {order.status || "pending"}
                          </span>
                        </td>
                        <td>
                          <div className="small fw-medium">
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleDateString()
                              : "—"}
                          </div>
                          <small className="text-muted">
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                              : ""}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary rounded-pill"
                              onClick={() => handleViewBill(order)}
                              title="View Invoice"
                            >
                              <i className="bi bi-receipt"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-info rounded-pill"
                              onClick={() => handleViewOrderDetails(order)}
                              title="View Details & Manage"
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <div className="text-muted">
                            <i className="bi bi-inbox display-4 d-block mb-3"></i>
                            <h5>No orders found</h5>
                            <p>Try adjusting your filters or search criteria</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="card-footer bg-light border-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Page {currentPage} of {totalPages}
                    </div>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === "products" && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold">Product Management</h5>
              <button
                className="btn btn-dark rounded-pill px-4"
                style={{ background: "#2f0f0c" }}
                onClick={handleAddProduct}
              >
                <i className="bi bi-plus-circle me-2"></i>Add Product
              </button>
            </div>

            {/* PRODUCT FORM */}
            {showProductForm && (
              <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                <h6 className="fw-bold mb-3">
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </h6>
                <form>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">
                        Product Name
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        placeholder="e.g., Signature Perfume"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">Type</label>
                      {customTypeMode ? (
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control rounded-start-3"
                            placeholder="e.g. Attar, Body Mist..."
                            value={customTypeInput}
                            onChange={(e) => setCustomTypeInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), confirmCustomType())}
                            autoFocus
                          />
                          <button type="button" className="btn btn-dark" style={{ background: "#2f0f0c" }}
                            onClick={confirmCustomType} title="Save type">
                            <i className="bi bi-check-lg"></i>
                          </button>
                          <button type="button" className="btn btn-outline-secondary rounded-end-3"
                            onClick={() => { setCustomTypeMode(false); setCustomTypeInput(""); }} title="Cancel">
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      ) : (
                        <select
                          className="form-select rounded-3"
                          name="type"
                          value={form.type}
                          onChange={handleTypeChange}
                        >
                          {DEFAULT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          {extraTypes.length > 0 && <option disabled>── Custom ──</option>}
                          {extraTypes.map(t => <option key={t} value={t}>{t}</option>)}
                          <option disabled>────────────</option>
                          <option value="__custom__">+ Add new type...</option>
                        </select>
                      )}
                      {/* Custom type tags with delete */}
                      {extraTypes.length > 0 && !customTypeMode && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {extraTypes.map(t => (
                            <span key={t}
                              className={`badge d-inline-flex align-items-center gap-1 px-2 py-1 ${
                                form.type === t ? "bg-dark" : "bg-secondary"
                              }`}
                              style={{ fontSize: 11, cursor: "pointer" }}
                              onClick={() => setForm(prev => ({ ...prev, type: t }))}
                            >
                              {t}
                              <i className="bi bi-x"
                                style={{ fontSize: 11, cursor: "pointer" }}
                                onClick={(e) => { e.stopPropagation(); deleteCustomType(t); }}
                                title={`Delete type "${t}"`}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">Gender</label>
                      <select
                        className="form-select rounded-3"
                        name="gender"
                        value={form.gender}
                        onChange={handleFormChange}
                      >
                        <option>Male</option>
                        <option>Female</option>
                        <option>All Genders</option>
                        <option>Combo Pack</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold">
                      Description
                    </label>
                    <textarea
                      className="form-control rounded-3"
                      name="description"
                      value={form.description}
                      onChange={handleFormChange}
                      rows="2"
                      placeholder="Product description"
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label small fw-bold">
                        Price (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        name="price"
                        value={form.price}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label small fw-bold">
                        Old Price (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control rounded-3"
                        name="oldPrice"
                        value={form.oldPrice}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label small fw-bold">Volume</label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        name="volume"
                        value={form.volume}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label small fw-bold">Season</label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        name="season"
                        value={form.season}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">Longevity</label>
                      <input type="text" className="form-control rounded-3" name="longevity" value={form.longevity} onChange={handleFormChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">Sillage</label>
                      <input type="text" className="form-control rounded-3" name="sillage" value={form.sillage} onChange={handleFormChange} />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label small fw-bold">Product Images</label>
                      <input
                        type="file"
                        className="form-control rounded-3"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                      />
                      <div className="form-text">Max 500KB each. Select multiple images. First image shown on product card.</div>
                      {imageUploading && (
                        <div className="mt-2 text-primary small">
                          <span className="spinner-border spinner-border-sm me-1"></span>Processing...
                        </div>
                      )}
                      {form.images?.length > 0 && (
                        <div className="mt-2 d-flex flex-wrap gap-2">
                          {form.images.map((img, idx) => (
                            <div key={idx} className="position-relative">
                              <img
                                src={img}
                                alt={`img-${idx}`}
                                className="rounded-2 border"
                                style={{ width: 70, height: 70, objectFit: "cover" }}
                              />
                              {idx === 0 && (
                                <span className="position-absolute top-0 start-0 badge bg-primary" style={{ fontSize: 9 }}>Main</span>
                              )}
                              <button
                                type="button"
                                className="position-absolute top-0 end-0 btn btn-danger btn-sm p-0"
                                style={{ width: 18, height: 18, fontSize: 10, lineHeight: 1 }}
                                onClick={() => removeImage(idx)}
                              >×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-dark rounded-pill flex-grow-1"
                      style={{ background: "#2f0f0c" }}
                      onClick={handleSubmitProduct}
                    >
                      {editingProduct ? "Update Product" : "Create Product"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-dark rounded-pill"
                      onClick={() => setShowProductForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* PRODUCTS LIST */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Image</th>
                      <th>Product</th>
                      <th>Type</th>
                      <th>Gender</th>
                      <th>Price</th>
                      <th>Old Price</th>
                      <th>Volume</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {(p.images?.[0] || p.image) ? (
                            <img
                              src={p.images?.[0] || p.image}
                              alt={p.name}
                              className="rounded-2"
                              style={{ width: "50px", height: "50px", objectFit: "cover" }}
                            />
                          ) : (
                            <div className="rounded-2 bg-light d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px" }}>
                              <i className="bi bi-image text-muted"></i>
                            </div>
                          )}
                        </td>
                        <td className="fw-bold">{p.name}</td>
                        <td>{p.type}</td>
                        <td>{p.gender}</td>
                        <td>₹{p.price}</td>
                        <td className="text-muted">
                          {p.oldPrice ? `₹${p.oldPrice}` : "—"}
                        </td>
                        <td>{p.volume}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEditProduct(p)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteProduct(p.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === "users" && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold">User Management</h5>
              <div className="text-muted">
                Total Users: <span className="fw-bold">{users.length}</span>
              </div>
            </div>

            {loadingUsers ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading users...</p>
              </div>
            ) : (
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="border-0 fw-semibold">Avatar</th>
                        <th className="border-0 fw-semibold">Name</th>
                        <th className="border-0 fw-semibold">Email</th>
                        <th className="border-0 fw-semibold">Phone</th>
                        <th className="border-0 fw-semibold">Joined</th>
                        <th className="border-0 fw-semibold">User ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length > 0 ? users.map((user) => (
                        <tr key={user.uid}>
                          <td>
                            <img
                              src={getAvatarUrl(user.email)}
                              alt="Avatar"
                              className="rounded-circle"
                              style={{ width: "40px", height: "40px" }}
                            />
                          </td>
                          <td>
                            <div className="fw-medium">{user.name || "—"}</div>
                          </td>
                          <td>
                            <div>{user.email}</div>
                          </td>
                          <td>
                            <div>{user.phone || "—"}</div>
                          </td>
                          <td>
                            <div className="small">
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                            </div>
                            <small className="text-muted">
                              {user.createdAt ? new Date(user.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
                            </small>
                          </td>
                          <td>
                            <div className="small font-monospace">{user.uid?.slice(0, 12)}...</div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-people display-4 d-block mb-3"></i>
                              <h5>No users found</h5>
                              <p>No registered users in the system</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BANNERS TAB */}
        {activeTab === "banners" && (
          <div className="row">
            <div className="col-md-5">
              <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                <h5 className="fw-bold mb-3">{editingBanner ? '✏️ Edit Banner' : '➕ Add Banner'}</h5>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Title *</label>
                  <input className="form-control rounded-3" value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="e.g. Luxury Redefined" />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Subtitle</label>
                  <input className="form-control rounded-3" value={bannerForm.text} onChange={(e) => setBannerForm({ ...bannerForm, text: e.target.value })} placeholder="e.g. Discover your signature fragrance" />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Banner Image * (max 2MB)</label>
                  <input
                    type="file"
                    className="form-control rounded-3"
                    accept="image/*"
                    onChange={handleBannerImageUpload}
                  />
                  {bannerImageUploading && (
                    <div className="mt-2 text-primary small"><span className="spinner-border spinner-border-sm me-1"></span>Processing...</div>
                  )}
                  {bannerForm.image && !bannerImageUploading && (
                    <div className="mt-2 position-relative d-inline-block">
                      <img src={bannerForm.image} alt="preview" className="img-fluid rounded-3" style={{ maxHeight: 120, objectFit: 'cover' }} />
                      <button type="button" className="btn btn-danger btn-sm position-absolute top-0 end-0" style={{ padding: '2px 6px' }} onClick={() => setBannerForm((prev) => ({ ...prev, image: '' }))}>×</button>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Animation</label>
                  <select className="form-select rounded-3" value={bannerForm.animation} onChange={(e) => setBannerForm({ ...bannerForm, animation: e.target.value })}>
                    {ANIMATIONS.map((a) => <option key={a} value={a}>{a.replace('animate__', '')}</option>)}
                  </select>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-dark rounded-pill flex-grow-1" style={{ background: '#2f0f0c' }} onClick={saveBanner} disabled={bannerLoading}>
                    {bannerLoading ? 'Saving...' : editingBanner ? 'Update Banner' : 'Add Banner'}
                  </button>
                  {editingBanner && (
                    <button className="btn btn-outline-secondary rounded-pill" onClick={() => { setEditingBanner(null); setBannerForm({ title: '', text: '', image: '', animation: 'animate__fadeInLeft' }); }}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-7">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white fw-bold">🖼️ Banners ({banners.length})</div>
                {banners.length === 0 ? (
                  <div className="text-center p-5 text-muted">No banners yet. Add one to replace the default homepage slides.</div>
                ) : (
                  <div className="list-group list-group-flush">
                    {banners.map((b) => (
                      <div key={b.id} className="list-group-item d-flex align-items-center gap-3 py-3">
                        <img src={b.image} alt={b.title} style={{ width: 90, height: 55, objectFit: 'cover', borderRadius: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
                        <div className="flex-grow-1">
                          <div className="fw-bold">{b.title} {b.isFirst && <span className="badge bg-success ms-1">First</span>}</div>
                          {b.text && <div className="text-muted small">{b.text}</div>}
                          <div className="text-muted small">{b.animation?.replace('animate__', '')}</div>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-success" title="Set as first slide" onClick={() => setFirstBanner(b.id)}>⭐</button>
                          <button className="btn btn-outline-primary" onClick={() => { setEditingBanner(b); setBannerForm({ title: b.title, text: b.text || '', image: b.image, animation: b.animation || 'animate__fadeInLeft' }); }}>✏️</button>
                          <button className="btn btn-outline-danger" onClick={() => deleteBanner(b.id)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* THEME TAB */}
        {activeTab === "theme" && <ThemeManager />}

        {/* SPECIAL OFFERS TAB */}
        {activeTab === "special-offers" && (
          <div>
            <AdminSpecialOffers />
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === "analytics" && (
          <div>
            <div className="row g-4 mb-5">
              {/* Revenue Analytics */}
              <div className="col-md-6">
                <div className="card border-0 shadow-sm rounded-4 p-4">
                  <h5 className="fw-bold mb-4">Revenue Analytics</h5>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="text-center p-3 bg-light rounded-3">
                        <h4 className="fw-bold text-success mb-1">₹{totalRevenue.toLocaleString()}</h4>
                        <small className="text-muted">Total Revenue</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-3 bg-light rounded-3">
                        <h4 className="fw-bold text-primary mb-1">₹{(totalRevenue / (totalOrders || 1)).toLocaleString()}</h4>
                        <small className="text-muted">Avg Order Value</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-3 bg-light rounded-3">
                        <h4 className="fw-bold text-warning mb-1">{pendingOrders}</h4>
                        <small className="text-muted">Pending Orders</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-3 bg-light rounded-3">
                        <h4 className="fw-bold text-success mb-1">{completedOrders}</h4>
                        <small className="text-muted">Completed Orders</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Order Status Distribution */}
              <div className="col-md-6">
                <div className="card border-0 shadow-sm rounded-4 p-4">
                  <h5 className="fw-bold mb-4">Order Status Distribution</h5>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small">Pending Orders</span>
                      <span className="small fw-bold">{pendingOrders} ({totalOrders > 0 ? Math.round((pendingOrders / totalOrders) * 100) : 0}%)</span>
                    </div>
                    <div className="progress mb-3" style={{height: '8px'}}>
                      <div 
                        className="progress-bar bg-warning" 
                        style={{width: `${totalOrders > 0 ? (pendingOrders / totalOrders) * 100 : 0}%`}}
                      ></div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small">Completed Orders</span>
                      <span className="small fw-bold">{completedOrders} ({totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0}%)</span>
                    </div>
                    <div className="progress mb-3" style={{height: '8px'}}>
                      <div 
                        className="progress-bar bg-success" 
                        style={{width: `${totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0}%`}}
                      ></div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <small className="text-muted">Completion Rate: {totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0}%</small>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Performance Metrics */}
            <div className="row g-4 mb-5">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm rounded-4 p-4 text-center">
                  <div className="bg-primary-subtle rounded-3 p-3 mb-3 d-inline-block">
                    <i className="bi bi-graph-up text-primary" style={{fontSize: '24px'}}></i>
                  </div>
                  <h5 className="fw-bold">Growth Rate</h5>
                  <h3 className="text-success">+25%</h3>
                  <small className="text-muted">vs last month</small>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm rounded-4 p-4 text-center">
                  <div className="bg-success-subtle rounded-3 p-3 mb-3 d-inline-block">
                    <i className="bi bi-people text-success" style={{fontSize: '24px'}}></i>
                  </div>
                  <h5 className="fw-bold">Customer Satisfaction</h5>
                  <h3 className="text-success">98%</h3>
                  <small className="text-muted">positive feedback</small>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm rounded-4 p-4 text-center">
                  <div className="bg-warning-subtle rounded-3 p-3 mb-3 d-inline-block">
                    <i className="bi bi-clock text-warning" style={{fontSize: '24px'}}></i>
                  </div>
                  <h5 className="fw-bold">Avg Processing Time</h5>
                  <h3 className="text-warning">2.5 days</h3>
                  <small className="text-muted">order to delivery</small>
                </div>
              </div>
            </div>
            
            {/* Top Products */}
            <div className="card border-0 shadow-sm rounded-4 p-4">
              <h5 className="fw-bold mb-4">Top Selling Products</h5>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0 fw-semibold">Product</th>
                      <th className="border-0 fw-semibold">Category</th>
                      <th className="border-0 fw-semibold">Orders</th>
                      <th className="border-0 fw-semibold">Revenue</th>
                      <th className="border-0 fw-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.slice(0, 5).map((product, index) => (
                      <tr key={product.id}>
                        <td>
                          <div className="fw-medium">{product.name}</div>
                          <small className="text-muted">{product.volume}</small>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark">{product.type}</span>
                        </td>
                        <td className="fw-bold">{Math.floor(Math.random() * 50) + 10}</td>
                        <td className="fw-bold text-success">₹{(product.price * (Math.floor(Math.random() * 50) + 10)).toLocaleString()}</td>
                        <td>
                          <span className="badge bg-success-subtle text-success">
                            <i className="bi bi-trending-up me-1"></i>Active
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Bill Modal */}
      {showBill && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invoice</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowBill(false)}
                ></button>
              </div>
              <div className="modal-body">
                <Bill order={selectedOrder} onDownload={handleDownloadBill} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Order Details - #{selectedOrder.id?.slice(0, 8)}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowOrderDetails(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">Customer Information</h6>
                    <div className="mb-2"><strong>Name:</strong> {selectedOrder.name}</div>
                    <div className="mb-2"><strong>Email:</strong> {selectedOrder.email}</div>
                    <div className="mb-2"><strong>Phone:</strong> {selectedOrder.phone}</div>
                    <div className="mb-3"><strong>Address:</strong> {selectedOrder.address}, {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.pincode}</div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-3">Order Information</h6>
                    <div className="mb-2"><strong>Order ID:</strong> {selectedOrder.id?.slice(0, 8)}</div>
                    <div className="mb-2"><strong>Total Amount:</strong> ₹{selectedOrder.total?.toFixed(2)}</div>
                    <div className="mb-2">
                      <strong>Status:</strong> 
                      <span className={`badge ms-2 ${
                        selectedOrder.status === "pending" ? "bg-warning" : 
                        selectedOrder.status === "completed" ? "bg-success" : "bg-secondary"
                      }`}>
                        {selectedOrder.status || "pending"}
                      </span>
                    </div>
                    <div className="mb-3"><strong>Date:</strong> {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleDateString() : "—"}</div>
                  </div>
                </div>

                <h6 className="fw-bold mb-3">Items Ordered</h6>
                <div className="table-responsive mb-4">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.name}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.price}</td>
                          <td>₹{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {selectedOrder.status === "pending" && (
                  <div className="border-top pt-4">
                    <h6 className="fw-bold mb-3">Send Tracking Information</h6>
                    <div className="mb-3">
                      <label className="form-label">Tracking Link</label>
                      <input
                        type="url"
                        className="form-control"
                        value={trackingLink}
                        onChange={(e) => setTrackingLink(e.target.value)}
                        placeholder="Enter tracking URL (e.g., https://tracking.example.com/track/ABC123)"
                      />
                      <div className="form-text">Once sent, the order status will be updated to "Completed"</div>
                    </div>
                    <button
                      className="btn btn-success"
                      onClick={handleSendTrackingEmail}
                      disabled={sendingEmail || !trackingLink.trim()}
                    >
                      {sendingEmail ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Send Tracking Email
                        </>
                      )}
                    </button>
                  </div>
                )}

                {selectedOrder.status === "completed" && (
                  <div className="alert alert-success">
                    <i className="bi bi-check-circle me-2"></i>
                    This order has been completed and tracking information has been sent to the customer.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
